import React, { useEffect, useState } from 'react';
import CoachingHeading from 'coaching/components/CoachingHeading';
import LinkConstants from 'constants/LinkConstants';
import Button from 'components/Button';
import Copy from 'components/Copy';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import { getProspectiveOptions, ProspectiveOptionsResult } from 'coaching/actions/CoachingActions';
import TutoringSelfServeViewStudentDetails from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewStudentDetails';
import { sendABTastyCustomEvent } from 'coaching/lib/abTasty';
import EventTracking from 'lib/EventTracking';
import { pushCustomEvent } from 'lib/googleTagManager';
import { PurchaserRole } from 'globalTypes';
import {
  clearScheduleData,
  saveStudentDetails,
  UnauthedPurchaserData,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';

export type TutoringSelfServeViewStudentDetailsUnauthenticatedState = {
  selectedChildName?: string;
  selectedSubject?: string;
  selectedGradeLevel?: string;
  prospectiveOptionsResult: ProspectiveOptionsResult;
};

export type Props = {
  goToNextState: () => void;
  restoredProps: {
    unauthedPurchaserDetails: UnauthedPurchaserData;
    studentDetails: {
      selectedChildName?: string;
      selectedSubject: string;
      selectedGradeLevel: string;
    } | null;
  };
};

const eventPrefix = 'tutoring_self_schedule_purchase.student_details';

export default function TutoringSelfServeViewStudentDetailsUnauthenticated(props: Props) {
  const { goToNextState } = props;
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<TutoringSelfServeViewStudentDetailsUnauthenticatedState>(
    () => {
      const details = props.restoredProps.studentDetails;
      return {
        selectedChildName: details?.selectedChildName || '',
        selectedSubject: details?.selectedSubject || '',
        selectedGradeLevel: details?.selectedGradeLevel || '',
        prospectiveOptionsResult: { subjects: [], gradeLevels: [] },
      };
    }
  );
  const purchaserRole = props.restoredProps.unauthedPurchaserDetails.purchaserRole;
  const isParentPurchaser = purchaserRole === PurchaserRole.Parent;

  const getData = async () => {
    setLoading(true);
    const prospectiveOptions = await getProspectiveOptions();
    setState({
      ...state,
      prospectiveOptionsResult: prospectiveOptions,
    });
    setLoading(false);
  };

  const onContinue = () => {
    const previouslySavedSubject = props.restoredProps?.studentDetails?.selectedSubject;
    const changingPreviousSubject =
      previouslySavedSubject && previouslySavedSubject !== state.selectedSubject;
    if (changingPreviousSubject) {
      clearScheduleData();
    }

    saveStudentDetails({
      selectedChildName: state.selectedChildName,
      // we already validated that these are defined
      selectedSubject: state.selectedSubject!,
      selectedGradeLevel: state.selectedGradeLevel!,
    });
    EventTracking.trackKey(`${eventPrefix}.continue.click`, {
      data: JSON.stringify({
        student_name: state.selectedChildName,
        tutoring_grade_level: state.selectedGradeLevel,
        tutoring_subject: state.selectedSubject,
      }),
    });
    goToNextState();
  };

  useEffect(() => {
    getData();
    sendABTastyCustomEvent('tutoring_details');
    pushCustomEvent('coaching_details_screen');
    EventTracking.trackKey(`${eventPrefix}.view`);
  }, []);

  const { prospectiveOptionsResult } = state;
  const { subjects, gradeLevels } = prospectiveOptionsResult;
  const error = prospectiveOptionsResult.error;
  const { selectedGradeLevel, selectedSubject, selectedChildName } = state;
  const isContinueButtonDisabled =
    !selectedGradeLevel || !selectedSubject || (isParentPurchaser && !selectedChildName);

  if (loading) {
    return <CenteredLoadingIndicator />;
  }

  const errorText = 'Oops, looks like something went wrong - please try refreshing the page.';

  if (error) {
    return (
      <ErrorReporter
        error={`TutoringSelfServeViewDetailsUnauthenticated - could not load subjects e=${error}`}>
        {errorText}
      </ErrorReporter>
    );
  } else if (!subjects || !gradeLevels) {
    return (
      <ErrorReporter
        error={`TutoringSelfServeViewDetailsUnauthenticated - could not load subjects, grade levels
                subjects=${subjects} gradeLevel=${gradeLevels}`}>
        {errorText}
      </ErrorReporter>
    );
  }

  return (
    <React.Fragment>
      <CoachingHeading data-test="student-details-heading">Tell us a few details</CoachingHeading>
      <Row>
        <Copy data-test="student-details-header-copy" align="left" size="xsmall">
          We’ll use this information to select a Remind tutor who’s uniquely suited to work with{' '}
          {isParentPurchaser ? 'your student' : 'you'}. Currently, we focus on{' '}
          <Text weight="semibold">2nd–12th grade math subjects.</Text>
        </Copy>
      </Row>
      <Row size="large">
        <TutoringSelfServeViewStudentDetails
          showChildNameFormElement={isParentPurchaser}
          selectedChildName={state.selectedChildName}
          selectedGradeLevel={state.selectedGradeLevel}
          selectedSubject={state.selectedSubject}
          subjects={subjects}
          gradeLevels={gradeLevels}
          onChildNameChange={value => setState({ ...state, selectedChildName: value })}
          onGradeLevelChange={value => setState({ ...state, selectedGradeLevel: value })}
          onSubjectChange={value => setState({ ...state, selectedSubject: value })}
        />
        <Button
          fullAtBreakpoint="480"
          data-test="student-details-continue-button"
          color="koolaid"
          onClick={onContinue}
          disabled={isContinueButtonDisabled}>
          Continue
        </Button>
      </Row>
      <Text size="xsmall" color="graphite">
        Your student’s data is never sold or shared. We collect this information in order to provide
        our service. Learn more in our{' '}
        <Link href={LinkConstants.PRIVACY_POLICY} target="_blank">
          Privacy Policy.
        </Link>
      </Text>
    </React.Fragment>
  );
}
